import { Suspense, forwardRef, useEffect, useMemo, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Environment, Lightformer, ContactShadows, OrbitControls, Sparkles } from '@react-three/drei';
import * as THREE from 'three';
import { growthFor, shardPlan, shardScale, threshold, rng, hashSeed } from '../lib/crystal.js';

/**
 * A user's crystal: a low-poly mineral cluster on a rock, rendered in WebGL.
 *
 * Every point in the cluster has its own birth moment (see shardPlan), so the
 * cluster grows continuously with XP — new points push up from the rock and
 * older ones lengthen — instead of jumping at level boundaries.
 *
 * Props: xp (or level, for demos), hue (0..360), seed (string), size (px),
 *        interactive (drag to turn), charging, burst (change to pulse).
 */
export default function Crystal({ xp, level, hue = 270, seed = 'seed', size = 320,
                                  interactive = false, charging = false, burst = 0,
                                  spin = true, className = '', style }) {
  const g = growthFor(xp ?? threshold(level ?? 1));
  return (
    <div className={className} style={{ width: size, height: size, ...style }}>
      <Canvas dpr={[1, 2]}
              gl={{ antialias: true, alpha: true, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.05 }}
              camera={{ position: [0, 1.7, 4.6], fov: 32, near: 0.1, far: 50 }}
              style={{ background: 'transparent' }}>
        <Suspense fallback={null}>
          <Scene g={g} hue={hue} seed={seed} interactive={interactive} charging={charging} burst={burst} spin={spin} />
        </Suspense>
      </Canvas>
    </div>
  );
}

function Scene({ g, hue, seed, interactive, charging, burst, spin }) {
  const plan = useMemo(() => shardPlan(seed), [seed]);
  const rock = useMemo(() => makeRock(seed), [seed]);
  const cluster = useRef();
  const growth = useRef(0);           // eased toward g, so growth is watched, not cut to
  const pulse = useRef(0);            // decays after a burst
  const lastBurst = useRef(burst);
  const colors = useMemo(() => mineral(hue), [hue]);

  useEffect(() => { if (burst !== lastBurst.current) { lastBurst.current = burst; pulse.current = 1; } }, [burst]);

  useFrame((_, dt) => {
    growth.current += (g - growth.current) * Math.min(1, dt * 1.4);
    pulse.current = Math.max(0, pulse.current - dt * 0.8);
    if (cluster.current && spin && !interactive) cluster.current.rotation.y += dt * 0.12;
  });

  return (
    <>
      <Environment resolution={256} frames={1}>
        {/* Warm key, cool fill, a bright rim — what makes glass read as glass. */}
        <Lightformer intensity={3} color="#fff3e0" position={[-3, 4, 3]} scale={[4, 4, 1]} form="rect" />
        <Lightformer intensity={1.6} color="#dbe6f3" position={[4, 2, -2]} scale={[3, 5, 1]} form="rect" />
        <Lightformer intensity={4} color="#ffffff" position={[0, 6, -6]} scale={[10, 1, 1]} form="ring" />
        <Lightformer intensity={0.8} color="#e8dcc8" position={[0, -3, 0]} rotation={[Math.PI / 2, 0, 0]} scale={[10, 10, 1]} form="rect" />
      </Environment>
      <directionalLight position={[-3, 5, 3]} intensity={1.4} color="#fff4e2" />
      <ambientLight intensity={0.25} />

      <group ref={cluster} position={[0, -0.55, 0]}>
        <Rock geometry={rock} growth={growth} />
        {plan.map((s, i) => (
          <Shard key={i} shard={s} growth={growth} pulse={pulse} colors={colors} charging={charging} />
        ))}
        <ClusterSparkles g={g} color={colors.sparkle} charging={charging} />
        <Glow g={g} colors={colors} pulse={pulse} charging={charging} />
      </group>

      <ContactShadows position={[0, -0.56, 0]} opacity={0.35} scale={6} blur={2.4} far={2} color="#3a2e22" frames={1} />

      {interactive && (
        <OrbitControls enablePan={false} enableZoom={false} minPolarAngle={0.6} maxPolarAngle={1.5}
                       autoRotate autoRotateSpeed={0.6} makeDefault />
      )}
    </>
  );
}

// ---------------------------------------------------------------- shard
const UP = new THREE.Vector3(0, 1, 0);

function Shard({ shard, growth, pulse, colors, charging }) {
  const group = useRef();
  const mat = useRef();

  // Where it stands and which way it leans — computed once.
  const { position, quaternion } = useMemo(() => {
    const px = Math.cos(shard.angle) * shard.ring * 0.72;
    const pz = Math.sin(shard.angle) * shard.ring * 0.72;
    const py = 0.2 - shard.ring * shard.ring * 0.16;               // rock dome
    const target = new THREE.Vector3(
      Math.sin(shard.lean) * Math.cos(shard.angle), Math.cos(shard.lean), Math.sin(shard.lean) * Math.sin(shard.angle),
    ).normalize();
    const q = new THREE.Quaternion().setFromUnitVectors(UP, target);
    q.multiply(new THREE.Quaternion().setFromAxisAngle(UP, shard.twist));
    return { position: [px, py, pz], quaternion: q };
  }, [shard]);

  useFrame(() => {
    const s = shardScale(shard, growth.current);
    if (!group.current) return;
    group.current.visible = s > 0.001;
    // Width fills in faster than height: a point starts stubby and lengthens.
    const w = shard.width * (0.55 + 0.45 * Math.min(1, s * 1.6));
    const h = shard.length * (0.08 + 0.92 * s);
    group.current.scale.set(w, h, w);
    if (mat.current) mat.current.emissiveIntensity = (charging ? 0.7 : 0.38) + pulse.current * 1.2;
  });

  return (
    <group ref={group} position={position} quaternion={quaternion}>
      {/* prism, slightly tapered, sitting on its base */}
      <mesh position={[0, 0.5, 0]}>
        <cylinderGeometry args={[0.86, 1, 1, 6, 1]} />
        <CrystalMaterial ref={mat} colors={colors} />
      </mesh>
      {/* pyramidal termination */}
      <mesh position={[0, 1 + shard.tip / 2, 0]}>
        <coneGeometry args={[0.86, shard.tip, 6, 1]} />
        <CrystalMaterial colors={colors} />
      </mesh>
    </group>
  );
}

const CrystalMaterial = forwardRef(function CrystalMaterial({ colors }, ref) {
  return (
    <meshPhysicalMaterial ref={ref} flatShading
      color={colors.body} emissive={colors.emissive} emissiveIntensity={0.38}
      transmission={0.7} thickness={0.9} ior={1.5} roughness={0.14} metalness={0}
      clearcoat={0.7} clearcoatRoughness={0.15}
      attenuationColor={colors.deep} attenuationDistance={1.6}
      envMapIntensity={1.6} specularIntensity={1.2} />
  );
});

// ---------------------------------------------------------------- rock
function makeRock(seed) {
  const r = rng(hashSeed(seed + 'rock'));
  const geo = new THREE.DodecahedronGeometry(1, 1);
  const pos = geo.attributes.position;
  // Displace shared vertices outward by noise, then flatten the top so points can stand.
  const seen = new Map();
  for (let i = 0; i < pos.count; i++) {
    const key = `${pos.getX(i).toFixed(4)},${pos.getY(i).toFixed(4)},${pos.getZ(i).toFixed(4)}`;
    let k = seen.get(key);
    if (k === undefined) { k = 0.85 + r() * 0.3; seen.set(key, k); }
    const x = pos.getX(i) * k, z = pos.getZ(i) * k;
    let y = pos.getY(i) * k;
    y = y > 0.2 ? 0.2 + (y - 0.2) * 0.35 : y;
    pos.setXYZ(i, x * 0.92, y * 0.42, z * 0.92);
  }
  geo.computeVertexNormals();
  return geo;
}

function Rock({ geometry, growth }) {
  const ref = useRef();
  useFrame(() => {
    if (!ref.current) return;
    const s = 0.78 + 0.22 * growth.current;
    ref.current.scale.set(s, s, s);
  });
  return (
    <mesh ref={ref} geometry={geometry} position={[0, 0.02, 0]}>
      <meshStandardMaterial color="#6f655a" roughness={0.95} metalness={0.02} flatShading />
    </mesh>
  );
}

function ClusterSparkles({ g, color, charging }) {
  // Count is fixed per mount: changing it live leaves Sparkles drawing past
  // its buffer. Bucket by growth so it remounts only at coarse steps.
  const count = 3 + Math.round(g * 4) + (charging ? 3 : 0);
  return <Sparkles key={count} count={count} scale={[1.6, 1.4, 1.6]} position={[0, 0.8, 0]} size={1.6} speed={0.12} opacity={0.45} color={color} />;
}

// ---------------------------------------------------------------- glow
const haloTexture = (() => {
  let tex;
  return () => {
    if (tex) return tex;
    const c = document.createElement('canvas'); c.width = c.height = 256;
    const ctx = c.getContext('2d');
    const gr = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    gr.addColorStop(0, 'rgba(255,255,255,0.9)');
    gr.addColorStop(0.35, 'rgba(255,255,255,0.35)');
    gr.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gr; ctx.fillRect(0, 0, 256, 256);
    tex = new THREE.CanvasTexture(c);
    return tex;
  };
})();

function Glow({ g, colors, pulse, charging }) {
  const light = useRef();
  const halo = useRef();
  const base = (charging ? 2.2 : 1.2) * (0.5 + g * 0.8);
  useFrame(() => {
    if (light.current) light.current.intensity = base + pulse.current * 3;
    if (halo.current) halo.current.material.opacity = (charging ? 0.5 : 0.32) * (0.4 + g * 0.6) + pulse.current * 0.3;
  });
  return (
    <>
      <pointLight ref={light} position={[0, 0.7, 0]} color={colors.glow} intensity={base} distance={4} decay={2} />
      <sprite ref={halo} position={[0, 0.75, -0.2]} scale={[2.6 + g * 1.2, 2.6 + g * 1.2, 1]}>
        <spriteMaterial map={haloTexture()} color={colors.glow} transparent opacity={0.3}
                        blending={THREE.AdditiveBlending} depthWrite={false} />
      </sprite>
    </>
  );
}

// ---------------------------------------------------------------- colour
/** Mineral tones: desaturated body, deeper attenuation, pale sparkle. */
function mineral(hue) {
  const h = ((hue % 360) + 360) % 360;
  return {
    body:     new THREE.Color().setHSL(h / 360, 0.42, 0.66),
    deep:     new THREE.Color().setHSL(h / 360, 0.55, 0.38),
    emissive: new THREE.Color().setHSL(h / 360, 0.55, 0.55),
    glow:     new THREE.Color().setHSL(h / 360, 0.6, 0.7),
    sparkle:  `hsl(${h} 60% 88%)`,
  };
}

/** A tiny flat glyph for places that cannot afford a WebGL context (the rail). */
export function CrystalGlyph({ hue = 270, size = 28, growth = 0.5 }) {
  const c1 = `hsl(${hue} 45% 62%)`, c2 = `hsl(${hue} 55% 40%)`, c3 = `hsl(${hue} 60% 85%)`;
  const h = 0.55 + growth * 0.45;
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden>
      <ellipse cx="16" cy="27" rx="12" ry="3.5" fill="#8c8073" opacity="0.6" />
      <polygon points={`10,26 12,${26 - 14 * h * 0.7} 14,${26 - 17 * h * 0.7} 16,26`} fill={c2} />
      <polygon points={`13,26 15,${26 - 18 * h} 17,${26 - 22 * h} 19,${26 - 18 * h} 21,26`} fill={c1} />
      <polygon points={`15,26 16,${26 - 20 * h} 17,${26 - 22 * h} 17,26`} fill={c3} opacity="0.8" />
      <polygon points={`19,26 21,${26 - 12 * h * 0.8} 23,${26 - 15 * h * 0.8} 25,26`} fill={c2} />
    </svg>
  );
}
